// Directory drag-and-drop adapter. Walks a DataTransferItemList using
// the File and Directory Entries API, reads every file as utf8 text,
// and returns a LoadedReceipt anchored under a synthetic package
// directory.

import type { LoadedReceipt, LoaderResult } from "./types.js";

const SYNTHETIC_ROOT = "/__receipt__";

interface FileEntry {
  relativePath: string;
  file: File;
}

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (cb: (file: File) => void, err?: (e: unknown) => void) => void;
  createReader?: () => {
    readEntries: (cb: (entries: FileSystemEntryLike[]) => void, err?: (e: unknown) => void) => void;
  };
}

export async function loadReceiptFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<LoaderResult> {
  // DataTransferItems become stale once the drop handler yields to the
  // event loop, so capture every entry reference synchronously up front.
  // The FileSystemEntry references stay valid after we leave the drop
  // handler; the DataTransferItem references do not.
  const rootEntries: FileSystemEntryLike[] = [];
  const looseFiles: File[] = [];
  for (let i = 0; i < dataTransfer.items.length; i++) {
    const item = dataTransfer.items[i];
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry();
    if (entry) {
      rootEntries.push(entry as FileSystemEntryLike);
      continue;
    }
    const file = item.getAsFile();
    if (file) looseFiles.push(file);
  }

  if (rootEntries.length === 0 && looseFiles.length === 0) {
    return {
      error: {
        code: "no_files",
        message: "No files were dropped. Drop a receipt directory."
      }
    };
  }

  const collected: FileEntry[] = [];
  for (const entry of rootEntries) {
    await walkEntry(entry, "", collected);
  }
  for (const file of looseFiles) {
    collected.push({ relativePath: file.name, file });
  }

  if (collected.length === 0) {
    return {
      error: {
        code: "empty_directory",
        message: "The dropped item contained no readable files."
      }
    };
  }

  return finalizeFileEntries(collected);
}

async function walkEntry(
  entry: FileSystemEntryLike,
  prefix: string,
  collected: FileEntry[]
): Promise<void> {
  const here = prefix ? `${prefix}/${entry.name}` : entry.name;
  if (entry.isFile && entry.file) {
    const file = await new Promise<File>((resolve, reject) => {
      entry.file!(resolve, reject);
    });
    collected.push({ relativePath: here, file });
    return;
  }
  if (entry.isDirectory && entry.createReader) {
    const reader = entry.createReader();
    // readEntries returns batches of up to 100 entries until empty.
    // We must keep calling it until we get an empty batch.
    let batch: FileSystemEntryLike[];
    do {
      batch = await new Promise<FileSystemEntryLike[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      for (const child of batch) {
        await walkEntry(child, here, collected);
      }
    } while (batch.length > 0);
  }
}

async function finalizeFileEntries(entries: FileEntry[]): Promise<LoaderResult> {
  const stripped = stripCommonRoot(entries);
  const sourceLabel = labelForEntries(entries);
  const files = new Map<string, string>();
  for (const { relativePath, file } of stripped) {
    files.set(relativePath, await file.text());
  }
  const loaded: LoadedReceipt = {
    packageDirectory: SYNTHETIC_ROOT,
    files,
    sourceLabel
  };
  return { receipt: loaded };
}

// Strip the common top-level directory from every entry's relative
// path so that the receipt root is anchored at packageDirectory.
//
// Three cases:
//
// 1. A single root containing a receipt.json (the user dropped the
//    receipt directory itself). Strip that root.
//
// 2. A single root that does not contain receipt.json (the user
//    dropped a parent directory). Strip the root anyway and let the
//    verifier surface a clear "no receipt envelope" failure.
//
// 3. Files at the top level (the user multi-selected files from the
//    receipt directory or dropped them individually). No common root
//    to strip; keep paths as-is.
function stripCommonRoot(entries: FileEntry[]): FileEntry[] {
  if (entries.length === 0) return entries;
  const tops = new Set<string>();
  let allHavePrefix = true;
  for (const { relativePath } of entries) {
    const slash = relativePath.indexOf("/");
    if (slash <= 0) {
      allHavePrefix = false;
      break;
    }
    tops.add(relativePath.slice(0, slash));
  }
  if (!allHavePrefix || tops.size !== 1) return entries;
  const root = `${Array.from(tops)[0]}/`;
  return entries.map(({ relativePath, file }) => ({
    relativePath: relativePath.startsWith(root) ? relativePath.slice(root.length) : relativePath,
    file
  }));
}

function labelForEntries(entries: FileEntry[]): string {
  if (entries.length === 0) return "dropped files";
  const first = entries[0].relativePath;
  const slash = first.indexOf("/");
  if (slash <= 0) return "dropped receipt";
  return first.slice(0, slash);
}

export async function loadReceiptFromInputFiles(fileList: FileList): Promise<LoaderResult> {
  const entries: FileEntry[] = [];
  for (const file of Array.from(fileList)) {
    const relativePath =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    entries.push({ relativePath, file });
  }
  if (entries.length === 0) {
    return {
      error: {
        code: "no_files",
        message: "No files were selected."
      }
    };
  }
  return finalizeFileEntries(entries);
}

export const RECEIPT_SYNTHETIC_ROOT = SYNTHETIC_ROOT;
